import React, {
  FC,
  MouseEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { Node } from 'reactflow';
import useStore from '@components/MainStage/store';
import { Modes } from '@constants/canvas/layers';
import { useProject } from '@context/Project/ProjectProvider';
import {
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import {
  activeCategoryCount,
  isCiaCategory,
  useModelFilters,
} from '@store/zustand/model-filters';
import { themePalette } from '@theme/muiTheme';
import { countNodesByValue } from '@utils/modelFilterCounts';
import { Tabs } from '@views/Insights/types';
import {
  StyledFilterButton,
  StyledFilterIcon,
} from '@views/Project/components/Filters/Filters.styled';
import { CATEGORY_ICONS } from '@views/Project/components/ModelFiltersPopover/categoryIcons';
import FilterCategoryPanel from '@views/Project/components/ModelFiltersPopover/FilterCategoryPanel';
import FilterCriteriaPanel from '@views/Project/components/ModelFiltersPopover/FilterCriteriaPanel';
import {
  FILTERS_POPOVER_OWNER_ATTRIBUTE,
  filtersPopoverOwnerProps,
  useFiltersPopover,
  useFiltersPopoverActions,
} from '@views/Project/components/ModelFiltersPopover/filtersPopover.store';
import FiltersRootPanel from '@views/Project/components/ModelFiltersPopover/FiltersRootPanel';
import { useCategoryOptions } from '@views/Project/components/ModelFiltersPopover/useCategoryOptions';

const NO_NODES: Node[] = [];

/**
 * The Filters funnel and its popover: the only entry point for model
 * filtering.
 *
 * Built on `Popper` inside a `ClickAwayListener`, not the Modal-backed
 * `Popover` every other popover in this repo uses: AC6.2 needs a click
 * outside to reach the element it lands on, and a Modal backdrop swallows that
 * first click. The listener wraps the trigger as well, so a click on the
 * funnel counts as inside and only `toggle` handles it (AC2.1) instead of a
 * close followed by a reopen; it fires on pointer-down, so the popover is gone
 * before the click it precedes is delivered. Without a Modal there is no free
 * Escape handling either, so while open a capture-phase `keydown` listener on
 * the document closes the popover and stops the event before the canvas's
 * bubble-phase Escape handlers see it (AC6.3). Both dismissals pause while the
 * unsaved-changes prompt is up: a tick that hides a dirty drawer's entity
 * opens `ConfirmDialog`, which is portaled outside the popover's DOM and React
 * trees, so the pointer-down that answers it would otherwise read as a click
 * away, and the Escape that cancels it would close the popover instead of
 * reaching the dialog (AC4.3).
 *
 * Root, criteria and values panels share one anchor and one paper (AC4.1.1).
 * A CIA category with one criteria opens its values directly; with several it
 * opens its criteria list first, and back retraces the same path. The
 * one-criteria shortcut is resolved while rendering rather than when the
 * category is chosen, so whoever opens a category needs no knowledge of its
 * criteria. Closing from any level resets to the root for the next open; the
 * selection lives in the store and dismissal never touches it (AC6.1).
 *
 * Open state lives in `filtersPopover.store` rather than here so an
 * applied-filter chip, or a row in the +N popover, can open it straight onto
 * a category and anchored to themselves. Elements marked with the owner
 * attribute are exempt from click-away for the same reason: they re-target
 * the popover in the same gesture that would otherwise dismiss it. When the
 * anchor leaves the document — a chip cleared from its own panel — the popover
 * re-anchors to the funnel instead of closing, so a change made inside it
 * never closes it; the funnel is registered with the store as that fallback
 * while this component is mounted, so the chip row can fall back to it too
 * when a chip unmounts in a render this component took no part in.
 * Unmounting closes it: the store is a module singleton that
 * nothing else resets, and a navigation while open (browser Back, a session
 * expiry redirect) reaches no dismissal, so without this the next toolbox to
 * mount would reopen on the previous project's panel, re-anchored to its own
 * funnel.
 *
 * Counts are computed only while the popover is open with nothing selected
 * anywhere (AC5.2), and the canvas nodes are subscribed to only then, so the
 * toolbox does not re-render on every node change while the popover is shut.
 * The disabled rule is the one the previous shell applied; QSP-3580 owns any
 * change to it.
 */
const ModelFiltersPopover: FC = () => {
  const {
    toolbox: { mode },
    isInsightsMode,
    insightsTab,
    isVersionsShown,
    isRiskRegisterMode,
    isUnsavedModalOpen,
  } = useProject();

  const { anchorEl, category, criteriaId } = useFiltersPopover();
  const {
    openRoot,
    showCategory,
    showRoot,
    setCriteriaId,
    setFallbackAnchor,
    reanchorIfDetached,
    close,
  } = useFiltersPopoverActions();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const state = useModelFilters();
  const activeCount = activeCategoryCount(state);
  const { options, criteria, labels, availability, vocabulary } =
    useCategoryOptions();

  const isOpen = Boolean(anchorEl);
  const needsCounts = isOpen && activeCount === 0 && mode !== null;
  const nodes = useStore((store) => (needsCounts ? store.nodes : NO_NODES));

  const isDisabled =
    (insightsTab !== Tabs.Default && isInsightsMode) ||
    isVersionsShown ||
    isRiskRegisterMode ||
    mode === Modes.RiskManagement;

  const counts = useMemo(
    () =>
      needsCounts && mode
        ? countNodesByValue(nodes, mode, vocabulary)
        : undefined,
    [needsCounts, mode, nodes, vocabulary],
  );

  useLayoutEffect(() => {
    setFallbackAnchor(triggerRef.current);

    return () => setFallbackAnchor(null);
  }, [setFallbackAnchor]);

  useLayoutEffect(() => {
    reanchorIfDetached();
  });

  useEffect(() => close, [close]);

  const isDismissible = isOpen && !isUnsavedModalOpen;

  useEffect(() => {
    if (!isDismissible) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      event.stopPropagation();
      close();
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isDismissible, close]);

  const toggle = (event: MouseEvent<HTMLElement>) => {
    if (isOpen) {
      close();

      return;
    }

    openRoot(event.currentTarget);
  };

  const handleClickAway = (event: globalThis.MouseEvent | TouchEvent) => {
    const { target } = event;

    if (
      target instanceof Element &&
      target.closest(`[${FILTERS_POPOVER_OWNER_ATTRIBUTE}]`)
    ) {
      return;
    }

    close();
  };

  const renderPanel = () => {
    if (!category) {
      return (
        <FiltersRootPanel
          onOpenCategory={showCategory}
          availability={availability}
          labels={labels}
          icons={CATEGORY_ICONS}
        />
      );
    }

    if (!isCiaCategory(category)) {
      return (
        <FilterCategoryPanel
          category={category}
          options={options[category] ?? []}
          counts={counts?.[category]}
          onBack={showRoot}
        />
      );
    }

    const list = criteria[category] ?? [];
    const current =
      list.find(({ id }) => id === criteriaId) ??
      (list.length === 1 ? list[0] : undefined);

    if (!current) {
      return (
        <FilterCriteriaPanel
          category={category}
          criteria={list}
          onOpenCriteria={setCriteriaId}
          onBack={showRoot}
        />
      );
    }

    const viaCriteriaList = list.length > 1;

    return (
      <FilterCategoryPanel
        category={category}
        title={viaCriteriaList ? current.name : undefined}
        options={current.options}
        counts={counts?.[category]?.[current.id]}
        criteriaId={current.id}
        onBack={viaCriteriaList ? () => setCriteriaId(undefined) : showRoot}
      />
    );
  };

  return (
    <ClickAwayListener
      onClickAway={handleClickAway}
      mouseEvent={isDismissible ? 'onPointerDown' : false}
      touchEvent={isDismissible ? 'onTouchStart' : false}
    >
      <Box position="relative" display="flex" alignItems="center">
        <StyledFilterButton
          ref={triggerRef}
          $isActive={isOpen}
          $isDisabled={isDisabled}
          disabled={isDisabled}
          onClick={toggle}
          aria-label="Filters"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          {...filtersPopoverOwnerProps}
        >
          <StyledFilterIcon fill={themePalette.grey[isDisabled ? 700 : 900]} />

          {activeCount > 0 && (
            <Stack
              data-testid="model-filters-badge"
              alignItems="center"
              justifyContent="center"
              sx={{
                position: 'absolute',
                top: 0,
                right: '-50%',
                transform: 'translate(-50%, -50%)',
                bgcolor: themePalette.primary.main,
                width: 20,
                height: 20,
                borderRadius: '50px',
              }}
            >
              <Typography variant="body2" color="grey.1000">
                {activeCount}
              </Typography>
            </Stack>
          )}
        </StyledFilterButton>

        <Popper
          open={isOpen}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: (theme) => theme.zIndex.modal }}
        >
          <Paper
            role="dialog"
            aria-label="Filters"
            elevation={0}
            sx={{
              width: 340,
              mt: '8px',
              borderRadius: '8px',
              overflow: 'hidden',
              bgcolor: themePalette.white,
              boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.10)',
            }}
          >
            {renderPanel()}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default ModelFiltersPopover;
